import { create } from 'zustand';
import { ToastOptions } from 'types/index';

interface Toast extends ToastOptions {
  id: string;
}

interface LoadingState {
  isLoading: boolean;
  loadingMessage?: string;
  loadingType?: 'overlay' | 'inline' | 'button';
}

interface UIState {
  isSidebarOpen: boolean;
  theme: 'light' | 'dark';
  toasts: Toast[];
  loading: LoadingState;
  loadingOperations: Map<string, LoadingState>;
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  toggleTheme: () => void;
  setTheme: (theme: 'light' | 'dark') => void;
  showToast: (options: ToastOptions) => void;
  removeToast: (id: string) => void;
  setLoading: (loading: boolean, message?: string, type?: 'overlay' | 'inline' | 'button') => void;
  setOperationLoading: (operationId: string, loading: boolean, message?: string, type?: 'overlay' | 'inline' | 'button') => void;
  clearOperationLoading: (operationId: string) => void;
  clearAllLoading: () => void;
}

export const useUIStore = create<UIState>((set, get) => ({
  isSidebarOpen: true,
  theme: 'light',
  toasts: [],
  loading: {
    isLoading: false,
    loadingMessage: undefined,
    loadingType: 'overlay',
  },
  loadingOperations: new Map(),

  toggleSidebar: () => {
    set((state) => ({ isSidebarOpen: !state.isSidebarOpen }));
  },

  setSidebarOpen: (open: boolean) => {
    set({ isSidebarOpen: open });
  },

  toggleTheme: () => {
    set((state) => {
      const newTheme = state.theme === 'light' ? 'dark' : 'light';
      document.documentElement.classList.toggle('dark', newTheme === 'dark');
      return { theme: newTheme };
    });
  },

  setTheme: (theme: 'light' | 'dark') => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
    set({ theme });
  },

  showToast: (options: ToastOptions) => {
    const id = `toast-${Date.now()}-${Math.random()}`;
    const toast: Toast = { id, ...options };
    
    set((state) => ({
      toasts: [...state.toasts, toast],
    }));

    setTimeout(() => {
      get().removeToast(id);
    }, options.duration || 5000);
  },

  removeToast: (id: string) => {
    set((state) => ({
      toasts: state.toasts.filter((toast) => toast.id !== id),
    }));
  },

  setLoading: (loading: boolean, message?: string, type: 'overlay' | 'inline' | 'button' = 'overlay') => {
    set({
      loading: {
        isLoading: loading,
        loadingMessage: message,
        loadingType: type,
      },
    });
  },

  setOperationLoading: (operationId: string, loading: boolean, message?: string, type: 'overlay' | 'inline' | 'button' = 'overlay') => {
    set((state) => {
      const newOperations = new Map(state.loadingOperations);
      if (loading) {
        newOperations.set(operationId, {
          isLoading: true,
          loadingMessage: message,
          loadingType: type,
        });
      } else {
        newOperations.delete(operationId);
      }
      return { loadingOperations: newOperations };
    });
  },

  clearOperationLoading: (operationId: string) => {
    set((state) => {
      const newOperations = new Map(state.loadingOperations);
      newOperations.delete(operationId);
      return { loadingOperations: newOperations };
    });
  },

  clearAllLoading: () => {
    set({
      loading: {
        isLoading: false,
        loadingMessage: undefined,
        loadingType: 'overlay',
      },
      loadingOperations: new Map(),
    });
  },
}));

